import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { toast } from 'sonner';
import { WorkoutFormValues, workoutSchema } from '../schema/workoutSchema';
import { useLanguagePath } from '@/utils/language';
import { useLanguage } from "@/components/LanguageContext";

export type Exercise = {
  name: string;
  sets: number;
  reps: string;
  weight?: string;
  notes?: string;
  image_url?: string;
  body_part?: string;
  target_muscle?: string;
  instructions?: string[];
  time?: string;
};

export type DatabaseExercise = {
  id: string;
  workout_id: string;
  day: number;
  sets: number;
  order_index: number;
  created_at: string;
  updated_at: string;
  name: string;
  reps: string;
  weight?: string;
  notes?: string;
  image_url?: string;
  body_part?: string;
  target_muscle?: string;
  instructions?: string[];
  time?: string;
};

export type WorkoutDay = {
  exercises: Exercise[];
  title?: string;
};

export type Direction = 'up' | 'down';

// Helper function to generate default day titles based on language
const generateDefaultDayTitle = (dayIndex: number, language: string): string => {
  switch (language) {
    case 'es':
      return `Día ${dayIndex + 1}`;
    case 'fr':
      return `Jour ${dayIndex + 1}`;
    case 'pt':
      return `Dia ${dayIndex + 1}`;
    case 'en':
    default:
      return `Day ${dayIndex + 1}`;
  }
};

export function useWorkoutForm(form: UseFormReturn<WorkoutFormValues>) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getPath } = useLanguagePath();
  const { language } = useLanguage();
  const queryClient = useQueryClient();
  const [workoutDays, setWorkoutDays] = useState<WorkoutDay[]>([{
    exercises: [],
    title: generateDefaultDayTitle(0, language)
  }]);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [areAllExpanded, setAreAllExpanded] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Update the default day title whenever language changes
  useEffect(() => {
    if (workoutDays.length > 0) {
      setWorkoutDays(prevDays => prevDays.map((day, index) => ({
        ...day,
        title: day.title || generateDefaultDayTitle(index, language)
      })));
    }
  }, [language]);

  const {
    data: existingWorkout
  } = useQuery({
    queryKey: ["workout", id],
    queryFn: async () => {
      if (!id) return null;
      
      const { data: customExercises } = await supabase
        .from("custom_exercises")
        .select("*");
      
      const customExerciseMap = new Map(
        customExercises?.map(ce => [ce.name.toLowerCase(), ce]) || []
      );
      
      const [workoutResponse, exercisesResponse] = await Promise.all([
        supabase.from("workouts").select(`
          *,
          target_student:students (
            id,
            first_name,
            last_name
          )
        `).eq("id", id).single(),
        
        supabase.from("exercises").select("*")
          .eq("workout_id", id)
          .order("day", { ascending: true })
          .order("order_index", { ascending: true })
      ]);
      
      const workoutError = workoutResponse.error;
      const exercisesError = exercisesResponse.error;
      
      if (workoutError) {
        console.error("Error fetching workout:", workoutError);
        throw workoutError;
      }
      
      if (exercisesError) {
        console.error("Error fetching exercises:", exercisesError);
        throw exercisesError;
      }
      
      const workout = workoutResponse.data;
      const exercises = exercisesResponse.data || [];

      const days: WorkoutDay[] = [];
      if (exercises.length > 0) {
        const dayMap = new Map<number, WorkoutDay>();
        
        exercises.forEach((exercise: DatabaseExercise) => {
          const dayIndex = exercise.day - 1;
          const customExercise = customExerciseMap.get(exercise.name.toLowerCase());
          
          if (!dayMap.has(dayIndex)) {
            dayMap.set(dayIndex, {
              exercises: [],
              title: `Dia ${dayIndex + 1}`
            });
          }
          
          const processedExercise = customExercise ? {
            ...exercise,
            image_url: customExercise.image_url,
            body_part: customExercise.body_part || exercise.body_part,
            target_muscle: customExercise.target_muscle || exercise.target_muscle,
            instructions: customExercise.instructions || exercise.instructions,
            isCustom: true
          } : {
            ...exercise,
            isCustom: false
          };
          
          dayMap.get(dayIndex)?.exercises.push(processedExercise);
        });
        
        for (let i = 0; i < Math.max(...dayMap.keys()) + 1; i++) {
          days[i] = dayMap.get(i) || { exercises: [], title: `Dia ${i + 1}` };
        }
      }
      
      return {
        ...workout,
        days
      };
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: students
  } = useQuery({
    queryKey: ["students"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("students").select("*").order("first_name");
      if (error) throw error;
      return data;
    }
  });

  useEffect(() => {
    if (existingWorkout) {
      console.log("Setting form values with workout:", existingWorkout);
      form.reset({
        title: existingWorkout.title,
        description: existingWorkout.description || "",
        type: existingWorkout.type || "",
        difficulty_level: existingWorkout.difficulty_level || "",
        duration: existingWorkout.duration || 60,
        equipment_needed: existingWorkout.equipment_needed || "",
        notes: existingWorkout.notes || "",
        days: existingWorkout.days?.length || 1,
        target_student_id: existingWorkout.target_student_id || ""
      });
      
      // Ensure that each day has a proper title based on current language
      const daysWithProperTitles = (existingWorkout.days || [{
        exercises: [],
        title: generateDefaultDayTitle(0, language)
      }]).map((day, index) => ({
        ...day,
        title: day.title || generateDefaultDayTitle(index, language)
      }));
      
      setWorkoutDays(daysWithProperTitles);
    }
  }, [existingWorkout, form, language]);

  const scrollToError = () => {
    const firstError = document.querySelector('[aria-invalid="true"]');
    if (firstError) {
      firstError.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  const toggleAllDays = () => {
    setAreAllExpanded(prev => !prev);
  };

  const moveDay = (fromIndex: number, direction: Direction) => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= workoutDays.length) return;
    setWorkoutDays(prev => {
      const newDays = [...prev];
      const [movedDay] = newDays.splice(fromIndex, 1);
      newDays.splice(toIndex, 0, movedDay);
      return newDays;
    });
  };

  const removeDay = (dayIndex: number) => {
    if (workoutDays.length > 1) {
      setWorkoutDays(prev => {
        const newDays = prev.filter((_, index) => index !== dayIndex);
        return newDays.map((day, index) => ({
          ...day,
          title: `Dia ${index + 1}${day.title?.replace(/^Dia \d+/, '') || ''}`
        }));
      });
      form.setValue("days", workoutDays.length - 1);
    }
  };

  const addDay = () => {
    const newDayNumber = workoutDays.length + 1;
    setWorkoutDays(prev => [...prev, {
      exercises: [],
      title: generateDefaultDayTitle(newDayNumber - 1, language)
    }]);
    const currentTitles = form.getValues("day_titles") || [];
    form.setValue("day_titles", [...currentTitles, ""]);
    form.setValue("days", workoutDays.length + 1);
  };

  const onSubmit = async (data: WorkoutFormValues) => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");
      
      const workoutData = {
        trainer_id: user.id,
        title: data.title,
        description: data.description,
        type: data.type,
        difficulty_level: data.difficulty_level,
        duration: Number(data.duration),
        equipment_needed: data.equipment_needed,
        notes: data.notes,
        days: data.days,
        target_student_id: data.target_student_id
      };

      const toastId = toast.loading("Salvando o treino...");

      const {
        data: workout,
        error: workoutError
      } = id ? 
        await supabase.from("workouts").update(workoutData).eq('id', id).select().single() : 
        await supabase.from("workouts").insert(workoutData).select().single();
      
      if (workoutError) throw workoutError;

      if (id) {
        const {
          data: existingExercises,
          error: fetchError
        } = await supabase.from("exercises").select("id, day, order_index").eq('workout_id', id);
        
        if (fetchError) throw fetchError;
        
        const existingExercisesMap = new Map();
        (existingExercises || []).forEach(exercise => {
          const key = `${exercise.day}-${exercise.order_index}`;
          existingExercisesMap.set(key, exercise.id);
        });
        
        const exercisesToUpdate = [];
        const exercisesToInsert = [];
        const exerciseIdsToKeep = new Set();
        
        for (let dayIndex = 0; dayIndex < workoutDays.length; dayIndex++) {
          const day = workoutDays[dayIndex];
          for (let exerciseIndex = 0; exerciseIndex < day.exercises.length; exerciseIndex++) {
            const exercise = day.exercises[exerciseIndex];
            const key = `${dayIndex + 1}-${exerciseIndex + 1}`;
            const exerciseData = {
              workout_id: workout.id,
              day: dayIndex + 1,
              order_index: exerciseIndex + 1,
              name: exercise.name,
              sets: exercise.sets,
              reps: exercise.reps,
              weight: exercise.weight,
              notes: exercise.notes,
              image_url: exercise.image_url,
              body_part: exercise.body_part,
              target_muscle: exercise.target_muscle,
              instructions: Array.isArray(exercise.instructions) ? exercise.instructions : undefined,
              time: exercise.time
            };

            const existingId = existingExercisesMap.get(key);
            if (existingId) {
              exercisesToUpdate.push({
                ...exerciseData,
                id: existingId
              });
              exerciseIdsToKeep.add(existingId);
            } else {
              exercisesToInsert.push(exerciseData);
            }
          }
        }
        
        const operations = [];
        
        if (exercisesToInsert.length > 0) {
          operations.push(
            supabase.from("exercises").insert(exercisesToInsert)
          );
        }
        
        for (const exercise of exercisesToUpdate) {
          const { id, ...data } = exercise;
          operations.push(
            supabase.from("exercises").update(data).eq('id', id)
          );
        }
        
        const results = await Promise.all(operations);
        
        for (const result of results) {
          if (result.error) throw result.error;
        }
        
        const exerciseIdsToDelete = (existingExercises || [])
          .filter(e => !exerciseIdsToKeep.has(e.id))
          .map(e => e.id);
        
        if (exerciseIdsToDelete.length > 0) {
          await supabase.from("exercises").delete().in('id', exerciseIdsToDelete);
        }
      } else {
        const exercisesToInsert = [];
        
        for (let dayIndex = 0; dayIndex < workoutDays.length; dayIndex++) {
          const day = workoutDays[dayIndex];
          for (let exerciseIndex = 0; exerciseIndex < day.exercises.length; exerciseIndex++) {
            const exercise = day.exercises[exerciseIndex];
            exercisesToInsert.push({
              workout_id: workout.id,
              day: dayIndex + 1,
              order_index: exerciseIndex + 1,
              name: exercise.name,
              sets: exercise.sets,
              reps: exercise.reps,
              weight: exercise.weight,
              notes: exercise.notes,
              image_url: exercise.image_url,
              body_part: exercise.body_part,
              target_muscle: exercise.target_muscle,
              instructions: Array.isArray(exercise.instructions) ? exercise.instructions : undefined,
              time: exercise.time
            });
          }
        }
        
        if (exercisesToInsert.length > 0) {
          const { error: insertError } = await supabase.from("exercises").insert(exercisesToInsert);
          if (insertError) throw insertError;
        }
      }
      
      toast.success(id ? "Treino atualizado com sucesso!" : "Treino criado com sucesso!", { id: toastId });
      
      queryClient.invalidateQueries({
        queryKey: ["workouts"]
      });
      
      navigate(getPath("/dashboard/workouts"));
    } catch (error: any) {
      console.error("Error saving workout:", error);
      toast.error(error.message || "Erro ao salvar treino");
    } finally {
      setIsSaving(false);
    }
  };

  const addExercise = (dayIndex: number) => {
    const newExercise: Exercise = {
      name: "",
      sets: 3,
      reps: ""
    };
    setWorkoutDays(prev => {
      const newDays = [...prev];
      newDays[dayIndex] = {
        ...newDays[dayIndex],
        exercises: [...newDays[dayIndex].exercises, newExercise]
      };
      return newDays;
    });
  };

  const removeExercise = (dayIndex: number, exerciseIndex: number) => {
    setWorkoutDays(prev => {
      const newDays = [...prev];
      newDays[dayIndex] = {
        ...newDays[dayIndex],
        exercises: newDays[dayIndex].exercises.filter((_, index) => index !== exerciseIndex)
      };
      return newDays;
    });
  };

  const updateExercise = (dayIndex: number, exerciseIndex: number, field: keyof Exercise, value: string | number | string[]) => {
    setWorkoutDays(prev => {
      const newDays = [...prev];
      const exercise = {
        ...newDays[dayIndex].exercises[exerciseIndex]
      };
      if (field === 'sets') {
        exercise[field] = Number(value);
      } else {
        exercise[field] = value as any;
      }
      newDays[dayIndex].exercises[exerciseIndex] = exercise;
      return newDays;
    });
  };

  const handleMoveExercise = (dayIndex: number, exerciseIndex: number, direction: Direction) => {
    const newIndex = direction === 'up' ? exerciseIndex - 1 : exerciseIndex + 1;
    if (newIndex < 0 || newIndex >= workoutDays[dayIndex].exercises.length) return;
    setWorkoutDays(prev => {
      const newDays = [...prev];
      const exercises = [...newDays[dayIndex].exercises];
      const [movedExercise] = exercises.splice(exerciseIndex, 1);
      exercises.splice(newIndex, 0, movedExercise);
      newDays[dayIndex] = {
        ...newDays[dayIndex],
        exercises
      };
      return newDays;
    });
  };

  const updateDayTitle = (dayIndex: number, newTitle: string) => {
    setWorkoutDays(prev => {
      const newDays = [...prev];
      newDays[dayIndex] = {
        ...newDays[dayIndex],
        title: newTitle || generateDefaultDayTitle(dayIndex, language)
      };
      return newDays;
    });
  };

  const generateWithAI = async (params: any) => {
    setIsGenerating(true);
    const toastId = toast.loading("Gerando treino com IA...");
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Usuário não autenticado", { id: toastId });
        return;
      }
      
      const response = await supabase.functions.invoke('generate-workout', {
        body: {
          ...params,
          userId: user.id,
          language: language
        }
      });
      
      if (response.error) {
        console.error("Erro na função generate-workout:", response.error);
        throw new Error(response.error.message || "Erro na geração do treino");
      }
      
      const workoutPlan = response.data;
      
      if (!workoutPlan?.days || !Array.isArray(workoutPlan.days)) {
        throw new Error('Formato de treino inválido');
      }

      form.reset({
        ...form.getValues(),
        title: workoutPlan.title || "",
        description: workoutPlan.description || "",
        notes: workoutPlan.notes || "",
        days: workoutPlan.days.length,
        type: params.type,
        difficulty_level: params.difficulty,
        equipment_needed: workoutPlan.equipment_needed || ""
      });
      
      // Process the workout plan, ensuring each day has a valid title
      const processedDays = workoutPlan.days.map((day: WorkoutDay, index: number) => ({
        ...day,
        title: day.title || generateDefaultDayTitle(index, language)
      }));
      
      setWorkoutDays(processedDays);
      
      const successMessage = {
        'pt': 'Treino gerado com sucesso!',
        'en': 'Workout generated successfully!',
        'es': '¡Entrenamiento generado con éxito!',
        'fr': 'Entraînement généré avec succès!'
      };
      
      toast.success(successMessage[language as keyof typeof successMessage] || 'Workout generated successfully!', { id: toastId });
    } catch (error: any) {
      console.error('Erro ao gerar treino:', error);
      
      const errorMessage = {
        'pt': `Erro ao gerar treino: ${error.message || "Erro desconhecido"}`,
        'en': `Error generating workout: ${error.message || "Unknown error"}`,
        'es': `Error al generar entrenamiento: ${error.message || "Error desconocido"}`,
        'fr': `Erreur lors de la génération de l'entraînement: ${error.message || "Erreur inconnue"}`
      };
      
      toast.error(errorMessage[language as keyof typeof errorMessage] || `Error generating workout: ${error.message || "Unknown error"}`, { id: toastId });
    } finally {
      setIsGenerating(false);
      setIsAIModalOpen(false);
    }
  };

  return {
    formRef,
    workoutDays,
    isAIModalOpen,
    setIsAIModalOpen,
    isGenerating,
    areAllExpanded,
    toggleAllDays,
    addDay,
    moveDay,
    removeDay,
    addExercise,
    removeExercise,
    updateExercise,
    handleMoveExercise,
    updateDayTitle,
    onSubmit,
    generateWithAI,
    students
  };
}
